import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '@/utils/i18n'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'

import { getLeadStageCounts, listInspectionLeads } from '@/api/inspection-leads'
import {
  deriveLeadStage,
  leadStatusOrder,
  leadStatusRank,
  leadStatusLabelKey,
  type InspectionLeadRow,
  type InspectionLeadStage,
  type InspectionLeadStatus,
  type LeadPipeline,
  leadPipelineUi,
 leadIsTest } from '@/types/inspection-lead'
import { dateFnsLocale } from '@/utils/dateLocale'
import { MoreHorizontal } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Popover } from '@/components/ui/Popover'
import { Loader } from '@/components/ui/Loader'
import { LoadingSecondsBanner } from '@/components/ui/LoadingSecondsBanner'
import { SearchInput } from '@/components/ui/SearchInput'
import { Select } from '@/components/ui/Select'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { TestAvitoLeadDialog } from '@/components/inspections/TestAvitoLeadDialog'
import { ManualLeadDialog } from '@/components/inspections/ManualLeadDialog'
import { SberAutoLeadDialog } from '@/components/inspections/SberAutoLeadDialog'
import { Button } from '@/components/ui/Button'
import { LeadKanbanBoard } from '@/components/inspections/LeadKanbanBoard'
import {
  KanbanCityFilter,
  KanbanCityFilterBanner,
} from '@/components/orders/KanbanCityFilter'
import { StatusBadge, SourceBadge, TestBadge } from '@/components/inspections/LeadBadges'
import { StatusBadge as OrderStatusBadge } from '@/components/orders/StatusBadge'
import { LotBadge } from '@/components/inspections/LotBadge'
import { emptyTableCellClassName } from '@/constants/ui'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useUiStore } from '@/store/uiStore'
import { formatPhoneDisplay } from '@/utils/phone'
import type { OrderStatus } from '@/types/enums'

const PAGE_SIZE = 100

type StatusFilter = InspectionLeadStatus | 'all'
type ViewMode = 'table' | 'kanban'

// ── Сортировка таблицы (клиентская: страница грузится целиком, PAGE_SIZE=100) ─
type SortKey =
  | 'no' | 'when' | 'source' | 'status' | 'assigned' | 'order' | 'lot' | 'vehicle' | 'client' | 'location'
type SortDir = 'asc' | 'desc'

// Только ключи; человеческие подписи резолвятся в render через t('leads.list.col.<key>')
// (t() нельзя звать на уровне модуля).
const TABLE_COLUMNS: SortKey[] = [
  'no',
  'when',
  'source',
  'status',
  'assigned',
  'order',
  'lot',
  'vehicle',
  'client',
  'location',
]

// Значение для сортировки по колонке. Статус — по позиции в воронке (а не
// по алфавиту); пустые значения уводим вниз независимо от направления.
const SORT_ACCESSORS: Record<SortKey, (r: InspectionLeadRow) => string | number | null> = {
  no: (r) => r.lead_no,
  when: (r) => new Date(r.created_at).getTime(),
  source: (r) => r.source ?? '',
  status: (r) => leadStatusRank(r.status, r.pipeline),
  assigned: (r) => r.assigned_to_name ?? null,
  order: (r) => r.order_status ?? null,
  lot: (r) => (leadPipelineUi(r.pipeline).lotBadgeStatuses.includes(r.status) ? (r.lot_status ?? '') : null),
  vehicle: (r) =>
    [r.vehicle_brand, r.vehicle_model, r.vehicle_year].filter(Boolean).join(' ') || null,
  client: (r) => [r.client_name, r.client_phone].filter(Boolean).join(' ') || null,
  location: (r) => (r.location ?? '').trim() || null,
}

function compareForSort(
  a: string | number | null,
  b: string | number | null,
  dir: SortDir,
): number {
  const aEmpty = a === null || a === ''
  const bEmpty = b === null || b === ''
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1 // пустые всегда вниз
  if (bEmpty) return -1
  const cmp =
    typeof a === 'number' && typeof b === 'number'
      ? a - b
      : String(a).localeCompare(String(b), 'ru')
  return dir === 'asc' ? cmp : -cmp
}

const VIEW_STORAGE_KEY = 'inspection-leads.view'
const PIPELINE_STORAGE_KEY = 'inspection-leads.pipeline'

function loadViewMode(): ViewMode {
  return localStorage.getItem(VIEW_STORAGE_KEY) === 'table' ? 'table' : 'kanban'
}

function loadPipeline(): LeadPipeline {
  try {
    return localStorage.getItem(PIPELINE_STORAGE_KEY) === 'sberauto' ? 'sberauto' : 'avito'
  } catch {
    return 'avito'
  }
}

/**
 * /inspection-leads — лиды по воронкам (Avito / СберАвто): таблица и канбан.
 *
 * Стандарт UsersPage: PageHeader с filters, таблица в overflow-x-auto
 * rounded-xl border, dedupedSearch, LoadingSecondsBanner для фонового
 * refetch.
 */
export function InspectionLeadsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [view, setView] = useState<ViewMode>(loadViewMode)
  const [pipeline, setPipeline] = useState<LeadPipeline>(loadPipeline)
  const [status, setStatus] = useState<StatusFilter>('all')
  const [showSberDialog, setShowSberDialog] = useState(false)
  const [search, setSearch] = useState('')
  const [showTestDialog, setShowTestDialog] = useState(false)
  const [showManualDialog, setShowManualDialog] = useState(false)
  // Размер окна выборки для канбана. Растёт по клику «Показать ещё», чтобы
  // подгрузить старые карточки терминальных стадий (Успешно/Отменён).
  const [kanbanLimit, setKanbanLimit] = useState(PAGE_SIZE)
  const debouncedSearch = useDebouncedValue(search, 250)
  const cityFilter = useUiStore((s) => s.leadsCityFilter)
  const setCityFilter = useUiStore((s) => s.setLeadsCityFilter)

  const changeView = (v: ViewMode) => {
    setView(v)
    localStorage.setItem(VIEW_STORAGE_KEY, v)
  }

  // У воронок разные статусы — фильтр статуса сбрасываем при переключении.
  const changePipeline = (p: LeadPipeline) => {
    setPipeline(p)
    setStatus('all')
    try {
      localStorage.setItem(PIPELINE_STORAGE_KEY, p)
    } catch {
      /* приватный режим — просто не запоминаем */
    }
  }

  // В канбане колонки и есть статусы — фильтр по статусу не применяем,
  // тянем все, чтобы заполнить все колонки воронки.
  const effectiveStatus: StatusFilter = view === 'kanban' ? 'all' : status

  const pageSize = view === 'kanban' ? kanbanLimit : PAGE_SIZE
  const { data, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['inspection-leads', pipeline, effectiveStatus, debouncedSearch, pageSize],
    queryFn: () =>
      listInspectionLeads({
        pipeline,
        status: effectiveStatus,
        search: debouncedSearch,
        pageSize,
      }),
    // Пока грузится новая страница, показываем прежние данные — но только той
    // же воронки: иначе при переключении Avito → СберАвто доска на миг
    // раскладывала лиды Avito по колонкам СберАвто, а колонка со счётчиком
    // из базы выглядела пустой («Нет данных» + «Показать ещё»).
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[1] === pipeline ? keepPreviousData(prev) : undefined,
    staleTime: 15_000,
  })

  const items = data?.items ?? []
  const tableLoading = isFetching && (isPlaceholderData || !data)

  // Фильтр по городу (только канбан). Город лида — поле `location`. Список
  // городов берём из загруженных лидов плюс уже выбранные (чтобы выбор не
  // пропадал, если по нему сейчас нет лидов в выборке).
  const availableCities = (() => {
    const set = new Set<string>()
    for (const it of items) {
      const c = (it.location ?? '').trim()
      if (c) set.add(c)
    }
    for (const c of cityFilter) set.add(c)
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'))
  })()

  const kanbanItems =
    cityFilter.length === 0
      ? items
      : items.filter((it) => cityFilter.includes((it.location ?? '').trim()))

  // Реальные счётчики стадий по всей базе (канбан рендерит лишь окно новейших
  // лидов, иначе терминальные колонки недосчитывают). Учитывает поиск; фильтр
  // по городу — клиентский, поэтому при активном городе считаем по загруженным
  // карточкам (там выборка уже узкая и помещается в окно).
  const { data: stageCountsRaw } = useQuery({
    queryKey: ['lead-stage-counts', pipeline, debouncedSearch],
    queryFn: () => getLeadStageCounts(debouncedSearch, pipeline),
    enabled: view === 'kanban' && cityFilter.length === 0,
    staleTime: 15_000,
  })
  const stageCounts =
    cityFilter.length === 0
      ? stageCountsRaw
      : kanbanItems.reduce<Partial<Record<InspectionLeadStage, number>>>((acc, it) => {
          const st = deriveLeadStage(it.status, it.order_status)
          acc[st] = (acc[st] ?? 0) + 1
          return acc
        }, {})

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        title={t('leads.title')}
        titleAside={
          <SegmentedControl<LeadPipeline>
            aria-label={t('leads.pipeline.aria')}
            // Подписи разной длины («Avito» / «СберАвто»): ширина варианта по
            // тексту, иначе равные доли сжатого контрола обрезают длинную.
            className="!w-auto [&>button]:!flex-none"
            value={pipeline}
            onChange={changePipeline}
            options={[
              { value: 'avito', label: t('leads.pipeline.avito') },
              { value: 'sberauto', label: t('leads.pipeline.sberauto') },
            ]}
          />
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {pipeline === 'sberauto' ? (
              <Button type="button" variant="primary" onClick={() => setShowSberDialog(true)}>
                {t('leads.list.createSberLead')}
              </Button>
            ) : null}
            <SegmentedControl<ViewMode>
              aria-label={t('leads.list.viewAria')}
              className="!w-auto"
              value={view}
              onChange={changeView}
              options={[
                { value: 'table', label: t('leads.list.viewTable') },
                { value: 'kanban', label: t('leads.list.viewKanban') },
              ]}
            />
            <Popover
              ariaLabel={t('leads.list.actionsAria')}
              // Кнопка — у правого края страницы: меню раскрывается влево.
              align="right"
              buttonClassName="h-9 w-9 !p-0"
              trigger={<MoreHorizontal size={18} aria-hidden />}
            >
              {(close) => (
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    className="w-full whitespace-nowrap rounded-[var(--stryzh-radius-sm)] px-3 py-2 text-left text-sm text-[color:var(--stryzh-color-text-primary)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
                    onClick={() => {
                      close()
                      setShowManualDialog(true)
                    }}
                  >
                    {t('leads.list.createLead')}
                  </button>
                  <button
                    type="button"
                    className="w-full whitespace-nowrap rounded-[var(--stryzh-radius-sm)] px-3 py-2 text-left text-sm text-[color:var(--stryzh-color-text-primary)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
                    onClick={() => {
                      close()
                      setShowSberDialog(true)
                    }}
                  >
                    {t('leads.list.createSberLeadMenu')}
                  </button>
                  <button
                    type="button"
                    className="w-full whitespace-nowrap rounded-[var(--stryzh-radius-sm)] px-3 py-2 text-left text-sm text-[color:var(--stryzh-color-text-secondary)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
                    onClick={() => {
                      close()
                      setShowTestDialog(true)
                    }}
                  >
                    {t('leads.list.testLead')}
                  </button>
                </div>
              )}
            </Popover>
          </div>
        }
        filters={
          <>
            <SearchInput
              className="min-w-0 flex-1 !max-w-none"
              placeholder={t('leads.list.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {view === 'table' ? (
              <div className="w-[150px] shrink-0 sm:w-[220px]">
                <Select<StatusFilter>
                  value={status}
                  onChange={(v) => {
                    if (v) setStatus(v)
                  }}
                  options={[
                    { value: 'all', label: t('leads.list.allStatuses') },
                    ...leadStatusOrder(pipeline).map((s) => ({
                      value: s as StatusFilter,
                      label: t(leadStatusLabelKey('status', s, pipeline, (k) => i18n.exists(k))),
                    })),
                  ]}
                />
              </div>
            ) : (
              <KanbanCityFilter
                cities={availableCities}
                selected={cityFilter}
                onChange={setCityFilter}
              />
            )}
          </>
        }
      />

      {view === 'kanban' ? (
        tableLoading && items.length === 0 ? (
          <Loader centered className="py-8" />
        ) : (
          <>
            <KanbanCityFilterBanner
              selected={cityFilter}
              onChange={setCityFilter}
            />
            <LeadKanbanBoard
              rows={kanbanItems}
              pipeline={pipeline}
              stageCounts={stageCounts}
              loadingMore={isFetching}
              onLoadMore={() => setKanbanLimit((n) => n + 200)}
            />
          </>
        )
      ) : (
        <LeadsTable
          rows={items}
          loading={tableLoading}
          onOpen={(id) => navigate(`/inspection-leads/${id}`)}
        />
      )}

      {showTestDialog ? (
        <TestAvitoLeadDialog onClose={() => setShowTestDialog(false)} />
      ) : null}
      {showManualDialog ? (
        <ManualLeadDialog onClose={() => setShowManualDialog(false)} />
      ) : null}
      {showSberDialog ? (
        <SberAutoLeadDialog
          onClose={() => setShowSberDialog(false)}
          onSaved={() => {
            setShowSberDialog(false)
            changePipeline('sberauto')
          }}
        />
      ) : null}
    </div>
  )
}

function LeadsTable({
  rows,
  loading,
  onOpen,
}: {
  rows: InspectionLeadRow[]
  loading?: boolean
  onOpen: (id: string) => void
}) {
  const { t } = useTranslation()
  const isInitialLoading = Boolean(loading) && rows.length === 0
  const isBackgroundLoading = Boolean(loading) && rows.length > 0

  // Сортировка: клик по заголовку — asc, повторный клик — desc; по умолчанию
  // (sortKey=null) сохраняем порядок с сервера (created_at desc).
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows
    const acc = SORT_ACCESSORS[sortKey]
    return [...rows].sort((a, b) => compareForSort(acc(a), acc(b), sortDir))
  }, [rows, sortKey, sortDir])

  const onSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
      {isBackgroundLoading ? <LoadingSecondsBanner /> : null}
      <table className="w-full min-w-[980px] text-left text-sm">
        <thead className="bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
          <tr>
            {TABLE_COLUMNS.map((key) => {
              const label = t(`leads.list.col.${key}`)
              return (
                <th key={key} className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => onSort(key)}
                    className="inline-flex items-center gap-1 hover:text-[color:var(--stryzh-color-text-primary)]"
                    aria-label={t('leads.list.sortBy', { col: label })}
                  >
                    {label}
                    <span className="text-[10px] leading-none opacity-70">
                      {sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </button>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {isInitialLoading ? (
            <tr>
              <td colSpan={9} className={emptyTableCellClassName}>
                <Loader centered size="sm" />
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={9} className={emptyTableCellClassName}>
                {t('common.noData')}
              </td>
            </tr>
          ) : (
            sortedRows.map((r) => <LeadRow key={r.id} row={r} onOpen={onOpen} />)
          )}
        </tbody>
      </table>
    </div>
  )
}

function LeadRow({
  row,
  onOpen,
}: {
  row: InspectionLeadRow
  onOpen: (id: string) => void
}) {
  const { t } = useTranslation()
  const dateStr = format(new Date(row.created_at), 'd MMM HH:mm', { locale: dateFnsLocale() })
  const vehicle = [row.vehicle_brand, row.vehicle_model, row.vehicle_year]
    .filter(Boolean)
    .join(' ')
  const phone = row.client_phone ? formatPhoneDisplay(row.client_phone) : ''
  const clientLabel = [row.client_name, phone].filter(Boolean).join(' · ')

  return (
    <tr
      onClick={() => onOpen(row.id)}
      className="cursor-pointer border-t border-[color:var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
    >
      <td className="px-3 py-2 font-[family-name:var(--stryzh-font-family-mono)] text-[color:var(--stryzh-color-text-primary)]">
        {row.lead_no}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-[color:var(--stryzh-color-text-secondary)]">
        {dateStr}
      </td>
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1.5">
          <SourceBadge source={row.source} />
          {leadIsTest(row) ? <TestBadge /> : null}
        </span>
      </td>
      <td className="px-3 py-2">
        <StatusBadge status={row.status} pipeline={row.pipeline} />
      </td>
      <td className="px-3 py-2 max-w-[180px] truncate text-[color:var(--stryzh-color-text-secondary)]">
        {row.assigned_to_name ?? (
          <span className="text-[color:var(--stryzh-color-text-secondary)]">{t('common.noData')}</span>
        )}
      </td>
      <td className="px-3 py-2">
        {row.order_status ? (
          <OrderStatusBadge status={row.order_status as OrderStatus} />
        ) : (
          <span className="text-[color:var(--stryzh-color-text-secondary)]">{t('common.noData')}</span>
        )}
      </td>
      <td className="px-3 py-2">
        {leadPipelineUi(row.pipeline).lotBadgeStatuses.includes(row.status) ? (
          <LotBadge status={row.lot_status} />
        ) : (
          <span className="text-[color:var(--stryzh-color-text-secondary)]">{t('common.noData')}</span>
        )}
      </td>
      <td className="px-3 py-2 font-medium text-[color:var(--stryzh-color-text-primary)]">
        {vehicle || t('common.noData')}
      </td>
      <td className="px-3 py-2 max-w-[260px] truncate">
        {clientLabel || t('common.noData')}
      </td>
      <td className="px-3 py-2 max-w-[200px] truncate text-[color:var(--stryzh-color-text-secondary)]">
        {row.location ?? t('common.noData')}
      </td>
    </tr>
  )
}
